/**
 * Authentication Context for React (Identity Platform)
 *
 * Provides authentication state and methods to all components.
 * Uses Firebase SDK with Google Cloud Identity Platform.
 * Supports: Google Sign-In, Email/Password Sign-In
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  onAuthStateChange,
  signInWithGoogle,
  signInWithEmail,
  resetPassword,
  signOut as firebaseSignOut,
  getIdToken,
  getAuthErrorMessage,
  User,
} from '../firebase';

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  provider: string;
}

interface UserContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Convert Firebase User to AuthUser
 */
function toAuthUser(firebaseUser: User): AuthUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    provider: firebaseUser.providerData[0]?.providerId || 'unknown',
  };
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      if (firebaseUser) {
        setUser(toAuthUser(firebaseUser));
        setError(null);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Proactively refresh token every 45 minutes to prevent expiration during session
  useEffect(() => {
    if (!user) return;

    const REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes (tokens expire after 60 min)
    let failedAttempts = 0;
    const MAX_FAILED_ATTEMPTS = 3;

    const refreshToken = async () => {
      try {
        const token = await getIdToken(true); // Force refresh
        if (token) {
          console.log('[Auth] Token proactively refreshed');
          failedAttempts = 0; // Reset on success
        } else {
          // Token is null - user may have been signed out externally
          console.warn('[Auth] Token refresh returned null - signing out user');
          setUser(null);
          setError('Je sessie is verlopen. Log opnieuw in.');
        }
      } catch (err: unknown) {
        failedAttempts++;
        const authError = err as { code?: string; message?: string };
        console.error('[Auth] Proactive token refresh failed:', authError.code, authError.message);

        // After multiple failures, sign out the user
        if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
          console.error('[Auth] Token refresh failed multiple times - signing out');
          setUser(null);
          setError('Er is een probleem met je sessie. Log opnieuw in.');
        }
      }
    };

    // Refresh immediately on mount, then every 45 minutes
    refreshToken();
    const interval = setInterval(refreshToken, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [user]);

  const clearError = () => {
    setError(null);
  };

  const handleSignInWithGoogle = async () => {
    try {
      clearError();
      setLoading(true);
      await signInWithGoogle();
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    try {
      clearError();
      setLoading(true);
      await signInWithEmail(email, password);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      clearError();
      await resetPassword(email);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    }
  };

  const handleSignOut = async () => {
    try {
      clearError();
      await firebaseSignOut();
      setUser(null);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(authError.message || 'Uitloggen mislukt');
    }
  };

  /**
   * Get authorization headers for API requests
   */
  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const token = await getIdToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const value: UserContextType = {
    user,
    loading,
    error,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    resetPassword: handleResetPassword,
    signOut: handleSignOut,
    clearError,
    getAuthHeaders,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
